import React, {FC, useState, useEffect} from "react"
import {withRouter} from "react-router-dom"
import PropTypes from "prop-types"
import qs from "qs"
import links from "@irati/utils/links"

import {connect} from "@irati/store"
import {Email} from "@irati-web/fields/user"
import {H3} from "@irati/ui/src/Typography"
import {get} from "lodash"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import withBranding, {WithBranding} from "@irati/brand/withBranding"
import Flex from "@irati/ui/src/Flex"
import Spacing from "@irati/ui/src/Spacing"
import {ReduxState} from "@irati/store/types"
import History from "@irati/utils/history"
import Button from "@irati/ui/src/Button"
import ErrorText from "@irati/ui/src/ErrorText"
import authentication from "@irati/auth"
import ssoErrorHandler from "@irati/sso/services/errors/ssoErrorHandler"
import {getAuthURL} from "@irati/sso/services/sso"
import {initiateSSOLogin} from "@irati/sso/helpers/handleSSOFlow"
import loginService from "@irati/auth/services/login"
import {ValidationButton} from "@irati/ui/src/FormItem"
import tracking, {LoginAvailable, LoginProduct, LoginType} from "@irati-tracking/platform/platform"
import getTenantFromPath from "@irati/identity/helpers/getTenantFromPath"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import Form from "@irati/ui/src/Form"
import {perkboxLogo} from "@irati/identity/helpers/defaultTenant"

import {REDIRECT_STORAGE_KEY} from "../../constants/login"
import {BusinessLogo, TextDivider} from "./styled"
import {AuthUrlState} from "@irati/types"

interface Props extends WithBranding {
  load: Promise<any> | null
  isMobile: boolean
  error: string
  onClick: (email: string) => void
  onSSO: () => void
}

interface Context {
  apps: {[key: string]: string}
}

const EnterEmail: FC<Props> = ({error, load, branding, isMobile, onClick, onSSO, ...props}, {apps}: Context) => {
  const [email, setEmail] = useState(localStorage.getItem("userEmail") || "")
  const [ssoURL, setSsoURL] = useState<string | null>(null)
  const [ssoError, setSsoError] = useState<string | null>(null)
  const [useRedirect, setUseRedirect] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [submit, setSubmit] = useState<Promise<any> | null>(null)
  const isTenant = getTenantFromPath()

  const {formatMessage} = useIntl()
  const isLoginRoute = History.location.pathname.includes("welcome/login")

  useEffect(() => {
    if (isTenant) {
      const nextPathname = sessionStorage.getItem(REDIRECT_STORAGE_KEY)
      const search = nextPathname?.split("?")[1]
      const state: AuthUrlState = {}
      if (search) {
        const obj = qs.parse(search)
        if (obj.useRedirectSso) {
          setUseRedirect(true)
          const query = qs.stringify({nextPathname})
          state.url = `${links.getDomain(authentication.unAuthedBusiness().subdomain)}/welcome/login/idp?${query}`
        }
      }
      getAuthURL(authentication.unAuthedBusiness().uuid!, undefined, state)
        .then(({url}) => {
          setSsoURL(url!)
        })
        .catch((err) => {
          setSsoError(ssoErrorHandler(err))
        })
    }
  }, [])
  const redirectBackTo = () => {
    const {PUBLIC, ONBOARDING, WELCOME, HOME} = apps
    const nextPathname = sessionStorage.getItem(REDIRECT_STORAGE_KEY) || get(props, "location.state.nextPathname", "")
    const isLogin = nextPathname.includes(WELCOME)
    if (!nextPathname || isLogin) {
      return null
    }
    if (nextPathname.includes(PUBLIC) || nextPathname.includes(ONBOARDING)) {
      return null
    }
    if (authentication.apps().admin && nextPathname.includes(HOME)) {
      return null
    }

    return nextPathname
  }
  const singleSignOn = () => {
    setSsoError(null)
    tracking.loginOTPSubmit("sso", LoginType.ssoLogin)
    localStorage.setItem("login_detail", "sso sso_login")
    localStorage.setItem("login_type", LoginType.ssoLogin)

    const ssoSubmit: Promise<any> = initiateSSOLogin(ssoURL!, {useRedirect})
      .then((response) => {
        const {accessToken} = response
        return loginService.ssoLogin(accessToken).then(() => {
          const redirectionUrl = redirectBackTo()
          const isAdmin = !!authentication.apps().admin

          if (redirectionUrl) {
            return History.replace(redirectionUrl)
          }

          if (isAdmin && !isMobile) {
            return History.replace({pathname: "/"}, apps.ADMIN)
          } else {
            History.replace({pathname: "/"})
          }
        })
      })
      .catch((err) => {
        const message = ssoErrorHandler(err)
        setSsoError(message)
        tracking.loginOTPError("sso", LoginType.ssoLogin)
        tracking.loginErrorDetails(message, LoginType.ssoLogin)
      })
    setSubmit(ssoSubmit)
  }
  const showEmail = !ssoURL || (ssoURL && isTenant && authentication.unAuthedBusiness().showPasswordLogin)

  const handleOnInputChange = (value) => {
    setEmailError("")
    setEmail(value)
    localStorage.setItem("userEmail", value || "")
  }

  const handleSSORedirect = () => {
    localStorage.setItem("login_type", LoginType.ssoLogin)

    tracking.loginCTAClick("sso_cta")
    History.push({pathname: "welcome/login/password", state: {ssoRedirect: true}})
  }

  const handleOnClick = () => {
    tracking.loginOTPSubmit("email", undefined, undefined, email)
    onClick(email)
  }

  const handleClickPassword = () => {
    localStorage.setItem("login_type", LoginType.passwordLogin)
    tracking.loginCTAClick("password_cta")
  }
  useEffect(() => {
    setEmailError(error)
  }, [error])

  useEffect(() => {
    if (!showEmail && ssoURL) {
      onSSO()
    }
    if (getTenantFromPath() && !showEmail && ssoURL) {
      localStorage.setItem("login_available", LoginAvailable.sso)
    }
    if (getTenantFromPath() && showEmail && ssoURL) {
      localStorage.setItem("login_available", LoginAvailable.passwordAndSSO)
    }
    if (getTenantFromPath() && showEmail && !ssoURL) {
      localStorage.setItem("login_available", LoginAvailable.password)
    }
    if (!getTenantFromPath()) {
      localStorage.setItem("login_available", "")
    }
  }, [showEmail, ssoURL])

  useEffect(() => {
    if (!window.location.pathname.includes("/activate")) {
      localStorage.setItem("login_type", LoginType.otpLogin)
      localStorage.setItem("login_product", LoginProduct.login)
    }
  }, [])

  return (
    <Flex direction="column" alignItems="center">
      {!!emailError && (
        <Alert
          type={AlertTypes.error}
          title={emailError}
          subTitle="platform.oneTimePassword.enterEmail.error.text"
          onHide={() => setEmailError(null)}
        />
      )}
      {isTenant && branding?.businessLogoUrl !== perkboxLogo && <BusinessLogo src={formatMessage({id: branding?.businessLogoUrl})} />}
      <H3 marginBottom={2}>
        <FormattedMessage id="platform.oneTimePassword.enterEmail.title" />
      </H3>
      {showEmail && (
        <Flex direction="column" width="100%">
          <Form>
            <Email
              label={formatMessage({id: "platform.defaultLogin.fields.email.placeholder"})}
              placeholder={formatMessage({id: "platform.defaultLogin.fields.emailValidation.enterEmail"})}
              value={email}
              onChange={handleOnInputChange}
            />
            <ValidationButton load={load} mobileFull full primary onClick={handleOnClick}>
              <FormattedMessage id="platform.oneTimePassword.enterEmail.mobileBtn2" />
            </ValidationButton>
          </Form>
        </Flex>
      )}
      {isLoginRoute && showEmail && (
        <>
          <TextDivider textAlign="center" width="100%" marginTop={4} marginBottom={4}>
            <FormattedMessage id="platform.defaultLogin.tenantLogin.button.or" />
          </TextDivider>
          <ButtonRouterLink
            id="signInUsingPasswordBtn"
            full
            to="welcome/login/password"
            onClick={handleClickPassword}
            marginBottom={isTenant && !ssoURL ? 8 : 0}>
            <FormattedMessage id="platform.defaultLogin.tenantLogin.button.loginPassword" />
          </ButtonRouterLink>
          {!ssoURL && !isTenant && (
            <Button full onClick={handleSSORedirect} marginTop={1} marginBottom={4}>
              <FormattedMessage id="platform.defaultLogin.tenantLogin.button.singleSignOn2" />
            </Button>
          )}
          {ssoURL && isTenant && (
            <Flex width="100%" marginTop={1} marginBottom={4}>
              <Button full mobileFull load={submit} onClick={singleSignOn}>
                <FormattedMessage id="platform.defaultLogin.tenantLogin.button.singleSignOn2" />
              </Button>
              {ssoError && (
                <Spacing marginTop={2}>
                  <ErrorText>{formatMessage({id: ssoError})}</ErrorText>
                </Spacing>
              )}
            </Flex>
          )}
          <Alert
            type={AlertTypes.info}
            title="platform.defaultLogin.tenantLogin.alert.title"
            subTitle="platform.defaultLogin.tenantLogin.alert.subTitle"
            nonDismissable
          />
        </>
      )}
      {isLoginRoute && !showEmail && ssoURL && (
        <Flex width="100%" marginTop={1} marginBottom>
          <Button primary full mobileFull load={submit} onClick={singleSignOn}>
            {authentication.unAuthedBusiness().loginPageSsoButtonText || (
              <FormattedMessage id="platform.defaultLogin.tenantLogin.button.singleSignOn2" />
            )}
          </Button>
          {ssoError && (
            <Spacing marginTop={2}>
              <ErrorText>{formatMessage({id: ssoError})}</ErrorText>
            </Spacing>
          )}
        </Flex>
      )}
    </Flex>
  )
}

const mapStateToProps = ({mq}: ReduxState) => {
  return {
    isMobile: mq.lessThan.medium
  }
}

EnterEmail.contextTypes = {
  apps: PropTypes.object.isRequired
}

export default withRouter(connect(mapStateToProps)(withBranding(EnterEmail)))
